import React, { useState, useEffect } from 'react';

import BadgeIcon from '@mui/icons-material/Badge';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { CompanyDetails } from '../types';
import CompanyMembersModal from './CompanyMembersModal';
import GlobalCard from './GlobalCard';
import { getFollowers, GraphNode } from '../services/graphService';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

type Props = {
  companyDetails: CompanyDetails | null;
};

const AccordionBasicInformation: React.FC<Props> = ({ companyDetails }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const theme = useThemeContext();

  const [showAllMembers, setShowAllMembers] = useState(false);
  const [showAllFollowers, setShowAllFollowers] = useState(false);
  const [followers, setFollowers] = useState<GraphNode[]>([]);
  const [followersLoading, setFollowersLoading] = useState(false);

  const { members } = companyDetails || {};

  const maxAvatars = 2;
  const extraCount = members && members?.length > maxAvatars ? members?.length - maxAvatars : 0;
  const extraFollowersCount =
    followers && followers?.length > maxAvatars ? followers?.length - maxAvatars : 0;

  useEffect(() => {
    const fetchFollowers = async () => {
      if (companyDetails?.uuid) {
        setFollowersLoading(true);
        try {
          const response = await getFollowers(companyDetails.uuid);
          setFollowers(response.items);
        } catch (error) {
          console.error('Erro ao buscar seguidores:', error);
        } finally {
          setFollowersLoading(false);
        }
      }
    };

    fetchFollowers();
  }, [companyDetails?.uuid]);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="h6" fontWeight="bold">
            Informações Básicas
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack
          direction={isMobile ? 'column' : 'column'}
          spacing={isMobile ? 1 : 2}
          sx={{ width: '100%' }}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 1 : 2}
            sx={{
              flex: 1,
              width: '100%',
              height: '100%',
            }}
          >
            <Box sx={{ width: isMobile ? '100%' : '50%' }}>
              <GlobalCard icon={<PersonIcon />} title="Sobre">
                <></>
              </GlobalCard>
            </Box>
            <Box sx={{ width: isMobile ? '100%' : '50%' }}>
              <GlobalCard icon={<BookmarkIcon />} title="Tags">
                <></>
              </GlobalCard>
            </Box>
          </Stack>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 1 : 2}
            sx={{
              flex: 1,
              width: '100%',
              height: '100%',
            }}
          >
            <Box sx={{ width: isMobile ? '100%' : '50%' }}>
              <GlobalCard icon={<BadgeIcon />} title={`Membros da empresa (${members?.length})`}>
                <Stack
                  direction="column"
                  spacing={2}
                  py={2}
                  sx={{
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'start',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mt={4}>
                    {members?.length === 0 ? (
                      <Typography variant="subtitle1" color="text.secondary" mt={2}>
                        Nenhum membro vinculado a esta empresa
                      </Typography>
                    ) : (
                      <>
                        {members?.slice(0, maxAvatars).map((member, idx) => (
                          <Tooltip
                            key={member.user_uuid}
                            title={`${member.first_name} ${member.last_name}`}
                          >
                            <Avatar
                              src={member.photo_url}
                              alt={`${member.first_name} ${member.last_name}`}
                              sx={{ width: 48, height: 48 }}
                            />
                          </Tooltip>
                        ))}
                        {extraCount > 0 && (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="50%"
                            width={48}
                            height={48}
                            bgcolor={theme.background.secondary}
                            color={theme.primary}
                          >
                            <Typography variant="h6" fontWeight={700} color={theme.primary}>
                              +{extraCount}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>

                  {extraCount > 0 && (
                    <Box display="flex" justifyContent="center" mt={2} width="100%">
                      <Box
                        component="button"
                        onClick={() => setShowAllMembers(!showAllMembers)}
                        sx={{
                          display: 'flex',
                          width: '60%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          px: 3,
                          py: 1.5,
                          border: `1px solid ${theme.primary}`,
                          borderRadius: 2,
                          backgroundColor: showAllMembers ? theme.primary : 'transparent',
                          color: showAllMembers ? theme.background.primary : theme.primary,
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: showAllMembers ? theme.primary + 'CC' : theme.primary,
                            color: theme.background.primary,
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                        Ver Todos os Membros
                      </Box>
                    </Box>
                  )}
                </Stack>
              </GlobalCard>
            </Box>
            <Box sx={{ width: isMobile ? '100%' : '50%' }}>
              <GlobalCard icon={<GroupsIcon />} title={`Seguidores (${followers?.length || 0})`}>
                <Stack
                  direction="column"
                  spacing={2}
                  py={2}
                  sx={{
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'start',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mt={4}>
                    {followersLoading ? (
                      <Typography variant="subtitle1" color="text.secondary" mt={2}>
                        Carregando seguidores...
                      </Typography>
                    ) : followers?.length === 0 ? (
                      <Typography variant="subtitle1" color="text.secondary" mt={2}>
                        Nenhum seguidor ainda
                      </Typography>
                    ) : (
                      <>
                        {followers?.slice(0, maxAvatars).map(follower => (
                          <Tooltip key={follower.id} title={follower.name}>
                            <Avatar
                              src={follower.photo_url}
                              alt={follower.name}
                              sx={{ width: 48, height: 48 }}
                            />
                          </Tooltip>
                        ))}
                        {extraFollowersCount > 0 && (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="50%"
                            width={48}
                            height={48}
                            bgcolor={theme.background.secondary}
                            color={theme.primary}
                          >
                            <Typography variant="h6" fontWeight={700} color={theme.primary}>
                              +{extraFollowersCount}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>

                  {extraFollowersCount > 0 && (
                    <Box display="flex" justifyContent="center" mt={2} width="100%">
                      <Box
                        component="button"
                        onClick={() => setShowAllFollowers(!showAllFollowers)}
                        sx={{
                          display: 'flex',
                          width: '60%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          px: 3,
                          py: 1.5,
                          border: `1px solid ${theme.primary}`,
                          borderRadius: 2,
                          backgroundColor: showAllFollowers ? theme.primary : 'transparent',
                          color: showAllFollowers ? theme.background.primary : theme.primary,
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: showAllFollowers
                              ? theme.primary + 'CC'
                              : theme.primary,
                            color: theme.background.primary,
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                        Ver Todos os Seguidores
                      </Box>
                    </Box>
                  )}
                </Stack>
              </GlobalCard>
            </Box>
          </Stack>
        </Stack>

        <CompanyMembersModal
          members={members || []}
          open={showAllMembers}
          onClose={() => setShowAllMembers(false)}
        />

        <CompanyMembersModal
          members={followers.map(follower => ({
            user_uuid: follower.id,
            first_name: follower.name.split(' ')[0] || '',
            last_name: follower.name.split(' ').slice(1).join(' ') || '',
            photo_url: follower.photo_url,
            company_role: 'Seguidor',
            company_uuid: companyDetails?.uuid || '',
          }))}
          open={showAllFollowers}
          onClose={() => setShowAllFollowers(false)}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionBasicInformation;
